import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  TextField,
  Checkbox,
  Dropdown,
  Heading,
  Text,
  Flex,
  Button,
  Toast,
} from '@vibe/core'
import { Check, Warning } from '@vibe/icons'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'
import { JobDefinitionSelector } from './JobDefinitionSelector'
import type {
  RequirementFieldConfig,
  RequirementFormData,
  JobDefinitionSelectorOption,
} from '../../types'

interface FormErrors {
  [key: string]: string
}

interface JobDefinitionSelectorProps {
  value: string
  onChange: (
    jobDefinitionId: string,
    jobDefinition?: JobDefinitionSelectorOption
  ) => void
  error?: string
}

interface DynamicRequirementFormProps {
  onSubmit?: (data: RequirementFormData) => void
  onCancel?: () => void
  initialData?: Partial<RequirementFormData>
  isSubmitting?: boolean
  jobDefinitionSelector?: JobDefinitionSelectorProps
}

export const DynamicRequirementForm: React.FC<DynamicRequirementFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isSubmitting = false,
  jobDefinitionSelector,
}) => {
  const [fieldConfigs, setFieldConfigs] = useState<RequirementFieldConfig[]>([])
  const [formData, setFormData] = useState<RequirementFormData>({
    puesto_solicitado: initialData?.puesto_solicitado || '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(true)
  const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false)
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Load field configurations on component mount
  useEffect(() => {
    const loadFieldConfigurations = async () => {
      try {
        setIsLoading(true)
        const response = await apiService.getRequirementFields()
        setFieldConfigs(response.data.fields)

        // Initialize form data with default values for dynamic fields
        const initialFormData: RequirementFormData = {
          puesto_solicitado: initialData?.puesto_solicitado || '',
        }

        response.data.fields.forEach(field => {
          if (initialData && initialData[field.field_id] !== undefined) {
            initialFormData[field.field_id] = initialData[field.field_id]
          } else {
            // Set default values based on field type
            switch (field.field_type) {
              case 'textbox':
                initialFormData[field.field_id] = ''
                break
              case 'checkbox':
                initialFormData[field.field_id] = false
                break
              case 'dropdown':
                initialFormData[field.field_id] = ''
                break
            }
          }
        })

        setFormData(initialFormData)
      } catch (error) {
        console.error('Error loading field configurations:', error)
        setToast({
          message: getErrorMessage(error),
          type: 'negative',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadFieldConfigurations()
  }, [initialData])

  // Validation function
  const validateField = useCallback(
    (
      fieldId: string,
      value: any,
      fieldConfig?: RequirementFieldConfig
    ): string => {
      // Validate puesto_solicitado (always required)
      if (fieldId === 'puesto_solicitado') {
        if (!value?.trim()) return 'El puesto solicitado es obligatorio'
        if (value.length < 3) return 'Mínimo 3 caracteres'
        if (value.length > 100) return 'Máximo 100 caracteres'
        return ''
      }

      // Validate dynamic fields
      if (fieldConfig) {
        if (fieldConfig.is_required) {
          if (fieldConfig.field_type === 'textbox') {
            if (!value?.trim())
              return `${fieldConfig.field_name} es obligatorio`
          } else if (fieldConfig.field_type === 'dropdown') {
            if (!value?.trim())
              return `${fieldConfig.field_name} es obligatorio`
          }
          // Note: checkbox fields don't need required validation as false is a valid value
        }

        // Type-specific validation
        if (
          fieldConfig.field_type === 'textbox' &&
          value &&
          value.length > 500
        ) {
          return `${fieldConfig.field_name} no puede exceder 500 caracteres`
        }

        if (
          fieldConfig.field_type === 'dropdown' &&
          value &&
          fieldConfig.dropdown_options &&
          !fieldConfig.dropdown_options.includes(value)
        ) {
          return `${fieldConfig.field_name} debe ser una opción válida`
        }
      }

      return ''
    },
    []
  )

  // Validate entire form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {}

    // Validate puesto_solicitado
    const puestoError = validateField(
      'puesto_solicitado',
      formData.puesto_solicitado
    )
    if (puestoError) newErrors.puesto_solicitado = puestoError

    // Validate dynamic fields
    fieldConfigs.forEach(fieldConfig => {
      const fieldError = validateField(
        fieldConfig.field_id,
        formData[fieldConfig.field_id],
        fieldConfig
      )
      if (fieldError) newErrors[fieldConfig.field_id] = fieldError
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, fieldConfigs, validateField])

  // Handle form field changes
  const handleFieldChange = useCallback(
    (fieldId: string, value: any) => {
      setFormData(prev => ({
        ...prev,
        [fieldId]: value,
      }))

      // Clear error for this field if it exists
      if (errors[fieldId]) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors[fieldId]
          return newErrors
        })
      }
    },
    [errors]
  )

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmission(true)

    if (!validateForm()) {
      setToast({
        message: 'Por favor corrige los errores antes de continuar',
        type: 'negative',
      })
      return
    }

    if (onSubmit) {
      onSubmit(formData)
    }
  }, [formData, validateForm, onSubmit])

  // Handle form reset
  const handleReset = useCallback(() => {
    const resetData: RequirementFormData = {
      puesto_solicitado: '',
    }

    fieldConfigs.forEach(field => {
      switch (field.field_type) {
        case 'textbox':
          resetData[field.field_id] = ''
          break
        case 'checkbox':
          resetData[field.field_id] = false
          break
        case 'dropdown':
          resetData[field.field_id] = ''
          break
      }
    })

    setFormData(resetData)
    setErrors({})
    setHasAttemptedSubmission(false)
  }, [fieldConfigs])

  // Render field based on type
  const renderField = (fieldConfig: RequirementFieldConfig) => {
    const fieldValue = formData[fieldConfig.field_id]
    const fieldError = errors[fieldConfig.field_id]
    const hasError = hasAttemptedSubmission && fieldError

    switch (fieldConfig.field_type) {
      case 'textbox':
        return (
          <TextField
            key={fieldConfig.field_id}
            title={`${fieldConfig.field_name}${fieldConfig.is_required ? ' *' : ''}`}
            placeholder={`Ingresa ${fieldConfig.field_name.toLowerCase()}`}
            value={fieldValue || ''}
            onChange={value => handleFieldChange(fieldConfig.field_id, value)}
            validation={{
              status: hasError ? 'error' : undefined,
              text: fieldError,
            }}
            required={fieldConfig.is_required}
          />
        )

      case 'checkbox':
        return (
          <Box key={fieldConfig.field_id}>
            <Checkbox
              label={`${fieldConfig.field_name}${fieldConfig.is_required ? ' *' : ''}`}
              checked={fieldValue || false}
              onChange={checked =>
                handleFieldChange(fieldConfig.field_id, checked)
              }
            />
            {hasError && (
              <Text className="text-red-600 text-sm mt-1">{fieldError}</Text>
            )}
          </Box>
        )

      case 'dropdown':
        const options =
          fieldConfig.dropdown_options?.map(option => ({
            value: option,
            label: option,
          })) || []

        return (
          <Box key={fieldConfig.field_id} className="dropdown-overflow-visible">
            <Text className="mb-2 font-medium">
              {fieldConfig.field_name}
              {fieldConfig.is_required && ' *'}
            </Text>
            <Dropdown
              placeholder={`Seleccionar ${fieldConfig.field_name.toLowerCase()}`}
              size="small"
              options={options}
              value={options.find(opt => opt.value === fieldValue) || null}
              onChange={(selectedOption: any) => {
                const value = selectedOption?.value || ''
                handleFieldChange(fieldConfig.field_id, value)
              }}
            />
            {hasError && (
              <Text className="text-red-600 text-sm mt-1">{fieldError}</Text>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  if (isLoading) {
    return (
      <Box className="flex items-center justify-center p-8">
        <Text>Cargando configuración del formulario...</Text>
      </Box>
    )
  }

  return (
    <Box className="max-w-4xl mx-auto">
      {/* Toast notifications */}
      {toast && (
        <Toast
          open={true}
          onClose={() => setToast(null)}
          className="fixed top-4 right-4 z-50"
          type={toast.type}
        >
          {toast.message}
        </Toast>
      )}

      <Box className="bg-white rounded-lg shadow-sm p-8">
        <Box className="space-y-6">
          <Heading type="h2" className="mb-6 pb-3 border-b">
            Crear Requerimiento
          </Heading>

          {/* Job Definition Selector as Puesto field */}
          {jobDefinitionSelector && (
            <Box>
              <Text className="mb-3 font-medium">Puesto *</Text>
              <JobDefinitionSelector
                value={jobDefinitionSelector.value}
                onChange={jobDefinitionSelector.onChange}
                placeholder="Seleccionar puesto..."
                required
                error={jobDefinitionSelector.error}
                disabled={isSubmitting}
              />
            </Box>
          )}

          {/* Dynamic fields */}
          {fieldConfigs.length > 0 && (
            <Box className="space-y-4">
              <Heading type="h3" className="mb-4">
                Información Adicional
              </Heading>
              {fieldConfigs.map(renderField)}
            </Box>
          )}

          {/* Form validation summary */}
          {hasAttemptedSubmission && Object.keys(errors).length > 0 && (
            <Box className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <Flex align="center" gap="small" className="mb-2">
                <Warning className="text-red-600" />
                <Text className="font-medium text-red-800">
                  Por favor corrige los siguientes errores:
                </Text>
              </Flex>
              <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Box>
          )}

          {/* Success indicator */}
          {hasAttemptedSubmission && Object.keys(errors).length === 0 && (
            <Box className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <Flex align="center" gap="small">
                <Check className="text-green-600" />
                <Text className="font-medium text-green-800">
                  Formulario válido y listo para enviar
                </Text>
              </Flex>
            </Box>
          )}
        </Box>

        {/* Action buttons */}
        <Flex justify="space-between" className="mt-8 pt-6 border-t">
          <Flex gap="small">
            <Button kind="tertiary" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>

            <Button
              kind="tertiary"
              onClick={handleReset}
              disabled={isSubmitting}
              aria-label="Limpiar todos los campos del formulario"
            >
              Limpiar Formulario
            </Button>
          </Flex>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Crear Requerimiento
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default DynamicRequirementForm
